import { TFunction } from "i18next";
import { ItemMonitoringStatus } from "@common/components/ListDetailViews/common/RealTimeStatus/type";
import { Account } from "@services/account/types";
import { Device, MonitoringSource } from "@services/devices/types";
import {
  has3rdPartyManagement,
  hasRoomController,
  hasDMCA,
} from "./checkMonitoringSource";

export const getDetailedStatus = (
  device: Device,
  monitoringSource: MonitoringSource
) =>
  device.detailedStatuses?.find(
    (status) => status.monitoringSource === monitoringSource
  );

/**
 * @param accountParam will use device.room?.location?.account if not explicitely passed
 */

export const generateGetErrorMessageFunction = (
  t: TFunction,
  deviceParam?: Device,
  accountParam?: Account
) => {
  const account = accountParam ?? deviceParam?.room?.location?.account;

  return (
    source: MonitoringSource,
    monitoringStatus: ItemMonitoringStatus,
    deviceOverride?: Device
  ) => {
    const device = deviceOverride ?? deviceParam;

    if (device === undefined) return "";
    // Generic checks
    if (!account?.active) return t("Account not active");
    if (!account.commProxyEnabled) return t("Cloud connector offline");
    if (!deviceParam?.room?.location?.active) return t("Location not active");
    if (!deviceParam?.room?.inService) return t("Room not active");
    if (!deviceParam?.room?.managed) return t("Room not managed");
    if (!device?.active) return t("Device not active");

    const status =
      device.detailedStatuses?.find(
        (status) => status.monitoringSource === source
      ) ?? device.status;

    // Error messages depending on the monitoring source
    switch (source) {
      case MonitoringSource.CommProxy:
        if (monitoringStatus === "unknown") {
          return t("Unknown");
        }
      case MonitoringSource.RoomController:
        if (hasRoomController(device)) {
          if (status?.lastUpdated === undefined) {
            return t("Unable to retrieve status from room controller");
          }

          if (device.aggregator && !device.aggregator?.active) {
            return t("Room controller offline");
          }

          if (status.stale) {
            return t("Monitoring Stalled from room controller");
          }
        }
        break;
      case MonitoringSource._3rdPartyManagement:
        if (has3rdPartyManagement(device)) {
          if (status?.lastUpdated === undefined) {
            return t("Unable to retrieve status from aggregator");
          }

          if (device.aggregator && !device.aggregator?.active) {
            return t("Aggregator offline");
          }

          if (status?.stale) {
            return t("Monitoring Stalled from aggregator");
          }
        }
        break;
      case MonitoringSource.AVISPLMonitoringAgent:
        if (hasDMCA(device)) {
          if (status?.lastUpdated === undefined) {
            return t("Unable to retrieve status from DMCA");
          }

          if (status.lastUpdated && status.stale) {
            return t("Monitoring Stalled from DMCA");
          }
        }
        break;
      default:
        break;
    }

    if (
      (monitoringStatus === "error" ||
        monitoringStatus === "troubleshooting") &&
      !status?.reachable
    ) {
      return t("Unreachable");
    }

    if (monitoringStatus === "unknown") {
      return t("Unknown");
    }

    return "";
  };
};
